import { AudioManager } from "./AudioManager";

/**
 * 씬 매니페스트 단위로 리소스를 프리로드. 이미 로드된 것은 스킵.
 *
 * 사용 패턴:
 *   1. SceneManager.push(id) 가 씬 매니페스트를 읽음
 *   2. AssetLoader.preload(manifest.assets) 대기
 *   3. 씬 렌더 시작
 *
 * 실패한 에셋은 콘솔 경고만 남기고 앱을 멈추지 않는다. 없으면 씬이 폴백 비주얼로 계속 동작.
 */
export interface FontDescriptor {
    family: string;
    source?: string;
    descriptors?: FontFaceDescriptors;
}

export interface AssetManifest {
    images?: string[];
    audio?: string[];
    fonts?: FontDescriptor[];
}

class AssetLoaderImpl {
    private loadedImages = new Set<string>();
    private loadedAudio = new Set<string>();
    private loadedFonts = new Set<string>();

    /**
     * 씬 전환은 리소스 없다고 막히면 안 된다. 전체 프리로드에 **하드 타임아웃 2초** —
     * 그 안에 끝나지 않으면 포기하고 씬 전환 계속. 각 개별 작업도 3초 개별 타임아웃.
     */
    async preload(assets?: AssetManifest): Promise<void> {
        if (!assets) return;

        const imageTasks = (assets.images ?? [])
            .filter(src => !this.loadedImages.has(src))
            .map(src => this.loadImage(src));

        const audioSrcs = (assets.audio ?? []).filter(src => !this.loadedAudio.has(src));
        const audioTask = audioSrcs.length > 0
            ? AudioManager.preloadSfx(audioSrcs).then(() => audioSrcs.forEach(s => this.loadedAudio.add(s)))
            : Promise.resolve();

        const fontTasks = (assets.fonts ?? [])
            .filter(f => !this.loadedFonts.has(f.family))
            .map(f => this.loadFont(f));

        const all = Promise.allSettled([...imageTasks, audioTask, ...fontTasks]);
        const hardCap = new Promise<void>(r => window.setTimeout(r, 2000));
        await Promise.race([all, hardCap]);
    }

    private loadImage(src: string): Promise<void> {
        return new Promise((resolve) => {
            let settled = false;
            const finish = () => { if (!settled) { settled = true; resolve(); } };
            const img = new Image();
            img.onload = () => { this.loadedImages.add(src); finish(); };
            img.onerror = () => {
                console.warn(`[AssetLoader] image failed: ${src}`);
                finish();
            };
            img.src = src;
            window.setTimeout(() => {
                if (!settled) console.warn(`[AssetLoader] image timeout: ${src}`);
                finish();
            }, 3000);
        });
    }

    private async loadFont(font: FontDescriptor): Promise<void> {
        try {
            if (font.source) {
                const face = new FontFace(font.family, `url(${font.source})`, font.descriptors);
                await face.load();
                document.fonts.add(face);
            } else {
                // 이미 @font-face 로 선언된 경우 — 로드 트리거만.
                await document.fonts.load(`16px "${font.family}"`);
            }
            this.loadedFonts.add(font.family);
        } catch (err) {
            console.warn(`[AssetLoader] font failed: ${font.family}`, err);
        }
    }

    isLoaded(src: string): boolean {
        return this.loadedImages.has(src) || this.loadedAudio.has(src) || this.loadedFonts.has(src);
    }
}

export const AssetLoader = new AssetLoaderImpl();
